import type { Principal } from '../api/types';

/** The person's role in this company, or null when they are not a member (staff under a grant, a stranger). */
export function roleIn(principal: Principal, tenantId: string): string | null {
  return principal.tenants.find((t) => t.id === tenantId)?.role ?? null;
}

/** Runs the company: people, billing, every job. Staff count, under their grant. */
export function isAdmin(principal: Principal, tenantId: string): boolean {
  return principal.staff || roleIn(principal, tenantId) === 'admin';
}

/**
 * Reads who is in the company and which API keys it has: the company's admins, whom alone the API
 * answers (`PeopleReads`), and Paige staff looking in under a support grant, who hold no role of
 * their own here. Everyone else is not asked for the lists at all: the menu leaves People and API
 * keys out, and the pages say whom to ask.
 */
export function readsPeople(principal: Principal, tenantId: string): boolean {
  const role = roleIn(principal, tenantId);
  return role === 'admin' || (principal.staff && role === null);
}

/** Runs jobs: switches one off and on, changes its setup (the API's manager scope). Admins, managers, staff. */
export function runsJobs(principal: Principal, tenantId: string): boolean {
  return seesBilling(principal, tenantId);
}

/**
 * Sees what the company spends (IMPLEMENTATION.md row 49 c): admins and managers, and staff. A
 * reviewer or a scanner is refused by the API (`GET /company/billing` needs the manager scope), so
 * the app does not ask.
 */
export function seesBilling(principal: Principal, tenantId: string): boolean {
  const role = roleIn(principal, tenantId);
  // The principal carries the API's tenant-role words (admin, manager, reviewer), not the people
  // list's membership roles (`SessionEndpoints`: job_manager becomes manager, the rest reviewer).
  return principal.staff || role === 'admin' || role === 'manager';
}
